import MultiPoint from 'ol/geom/MultiPoint';
import bbox from '@turf/bbox';
import { toWgs84 } from '@turf/projection';
import { fromLonLat } from 'ol/proj';

import {
  MAIN_MAP_ZOOM, MAIN_MAP_CENTER, WATERSHED_LAYER, DEFAULT_REGION_VARIABLE,
  DEFAULT_CLIMATE_VARIABLE, DEFAULT_MAP_ANIMATION_DURATION, WATERSHED_LAYER_NAME_FIELD,
  WATERSHED_LAYER_ID, HOME_BBOX,
} from '@/settings';
import SearchBox from '@/components/SearchBox/index.vue';
import RangeSelector from '@/components/RangeSelector.vue';
import ViewSelector from '@/components/ViewSelector.vue';
import MonthSelector from '@/components/MonthSelector.vue';
import ModelSelector from '@/components/ModelSelector.vue';
import WatershedView from '@/components/WatershedView.vue';
import ClimateChart from '@/components/ClimateChart/index.vue';
import RegionChart from '@/components/RegionChart.vue';
import ScaleView from '@/components/ScaleView.vue';
import MobileAltitudePreview from '@/components/MobileAltitudePreview.vue';
import pad from '@/lib/utils';

export default {
  name: 'explore_map',

  components: {
    SearchBox,
    RangeSelector,
    ViewSelector,
    MonthSelector,
    ModelSelector,
    WatershedView,
    RegionChart,
    ClimateChart,
    ScaleView,
    MobileAltitudePreview,
  },

  data() {
    return {
      // Although this properties have a default value in settings they are
      // subject to change at runtime
      zoom: MAIN_MAP_ZOOM,
      center: MAIN_MAP_CENTER,
      regionVariable: DEFAULT_REGION_VARIABLE,
      climateVariable: DEFAULT_CLIMATE_VARIABLE,

      // This properties are here to be changed at runtime
      highlightedFeatures: [],
      selectedWatershed: null,
      viewType: 'climate', // 'climate' or 'region'
      rawMonth: 1,
      range: 'all',
      model: '1950_2000',
      settingsLoaded: false,

      // This properties are used just to pass setting values to the template
      WATERSHED_LAYER,
      WMS_SERVER_URL: 'https://geoserver.carto.zone/geoserver/inecc/wms',
      WFS_SERVER_URL: 'https://geoserver.carto.zone/geoserver/inecc/wfs',
      WATERSHED_LAYER_NAME_FIELD,
      WATERSHED_LAYER_ID,
    };
  },

  computed: {
    climateLayer() {
      return `inecc:i${this.climateVariable}_${this.model}_${this.month}`;
    },

    regionLayer() {
      return `inecc:${this.regionVariable}`;
    },

    layerParams() {
      if (this.range === 'all') {
        return { CQL_FILTER: 'gridcode between 1 and 4' };
      }

      return {
        CQL_FILTER: `gridcode=${this.range}`,
      };
    },

    month() {
      return pad(this.rawMonth, 2);
    },

    scale() {
      if (this.climateVariable === 'prec') {
        return 'mm';
      }

      return '°C';
    },
  },

  created() {
    fetch('/settings.json')
      .then(res => res.json())
      .then((data) => {
        this.WMS_SERVER_URL = data.WMS_SERVER_URL;
        this.WFS_SERVER_URL = data.WFS_SERVER_URL;

        this.settingsLoaded = true;
      })
      .catch(() => {
        // use default values if anything fails
        this.settingsLoaded = true;
      });
  },

  mounted() {
    setTimeout(() => {
      this.fitToBbox(HOME_BBOX);
    }, 500);
  },

  watch: {
    model() {
      console.log(this.model);
    },

    climateLayer() {
      console.log(this.climateLayer);
    },

    selectedWatershed() {
      if (this.selectedWatershed === null) {
        this.fitToBbox(HOME_BBOX);
        this.highlightedFeatures = [];

        return;
      }

      const bb = bbox(this.selectedWatershed);
      this.fitToBbox(bb);
      this.highlightFeature(this.selectedWatershed);
    },
  },

  methods: {
    mapClick(event) {
      const url = this.$refs.mapSource.getFeatureInfoUrl(
        fromLonLat(event.coordinate),
        this.$refs.view.viewResolution,
        'EPSG:3857',
        { INFO_FORMAT: 'application/json' },
      );

      fetch(url)
        .then(res => res.json())
        .then((data) => {
          if (data.features.length === 0) {
            return;
          }

          this.selectedWatershed = toWgs84(data.features[0]);
        });
    },


    highlightFeature(feature) {
      this.highlightedFeatures = [feature];
    },

    searchResultFeatureMouseOver(feature) {
      this.highlightFeature(feature);
    },

    searchResultFeatureMouseLeave() {
      this.highlightedFeatures = [];
    },

    searchResultExtent(bb) {
      this.selectedWatershed = null;
      this.fitToBbox(bb);
    },

    fitToBbox(bb) {
      const geom = new MultiPoint([
        fromLonLat([bb[0], bb[1]]),
        fromLonLat([bb[2], bb[3]]),
      ]);

      this.$refs.view.fit(geom, {
        duration: DEFAULT_MAP_ANIMATION_DURATION,
      });
    },
  },
};
